import * as React from "react";
import { useHistory, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { Typography, Box, Button, Alert, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ReportJob, MetricsService, ReportJobStatus, ReportFileInfo } from "gen/clients";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { formatDate } from "utils/dateUtils";
import { FileTypesByClientReportTable } from "./FileTypesByClientReportTable";
import { ExportFileTypesByClientReportButton } from "./ExportFileTypesByClientReportButton";
import { NavMenuOption, PageLayout } from "components/PageLayout/PageLayout";
import { BreadcrumbItem } from "components/Breadcrumbs/Breadcrumbs";

interface RouteParams {
  reportId: string;
}

const FileTypesByClientReportDetailsPage: React.FC = () => {
  const { reportId } = useParams<RouteParams>();
  const history = useHistory();

  const {
    data: report,
    isLoading,
    error
  } = useQuery<ReportJob>(["getFileTypesByClientReport", reportId], {
    queryFn: () => MetricsService.getReportJob({ reportId }),
    refetchInterval: data => {
      return data?.status === ReportJobStatus.PENDING || data?.status === ReportJobStatus.RUNNING ? 5000 : false;
    }
  });

  const jsonFile = React.useMemo(() => {
    return report?.files?.find((file: ReportFileInfo) => file.format === ReportFileInfo.format.JSON);
  }, [report?.files]);

  const csvFile = React.useMemo(() => {
    return report?.files?.find((file: ReportFileInfo) => file.format === ReportFileInfo.format.CSV);
  }, [report?.files]);

  const handleBackClick = React.useCallback(() => {
    history.push("/analytics/fileTypesByClientReports");
  }, [history]);

  const breadcrumbs: BreadcrumbItem[] = [
    {
      path: "/analytics",
      label: "Analytics"
    },
    {
      path: "/analytics/fileTypesByClientReports",
      label: "File Types Uploaded by Client"
    },
    {
      path: `/analytics/fileTypesByClientReports/${reportId}`,
      label: `Report ${reportId}`,
      active: true
    }
  ];

  if (isLoading) {
    return (
      <PageLayout
        selectedNavMenu={NavMenuOption.ANALYTICS}
        title={<Typography variant="h6">File Types By Client Report - {reportId}</Typography>}
        breadcrumbs={breadcrumbs}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout
        selectedNavMenu={NavMenuOption.ANALYTICS}
        title={<Typography variant="h6">File Types By Client Report - {reportId}</Typography>}
        breadcrumbs={breadcrumbs}
      >
        <ApiErrorMessage apiError={error} />
      </PageLayout>
    );
  }

  if (!report) {
    return (
      <PageLayout
        selectedNavMenu={NavMenuOption.ANALYTICS}
        title={<Typography variant="h6">File Types By Client Report - {reportId}</Typography>}
        breadcrumbs={breadcrumbs}
      >
        <Alert severity="error">Report not found</Alert>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      selectedNavMenu={NavMenuOption.ANALYTICS}
      title={<Typography variant="h6">File Types By Client Report - {reportId}</Typography>}
      breadcrumbs={breadcrumbs}
    >
      <Box sx={{ mt: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={handleBackClick} sx={{ mb: 2 }}>
              Back to Reports
            </Button>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                File Types By Client Report - {reportId}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Status:</strong> {report.status}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Created by:</strong> {report.createdBy}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Created at:</strong> {formatDate(report.createdAt)}
              </Typography>
              {report.completedAt && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Completed at:</strong> {formatDate(report.completedAt)}
                </Typography>
              )}
              {report.params && (
                <>
                  {report.params.fromDate && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>From:</strong> {formatDate(report.params.fromDate)}
                    </Typography>
                  )}
                  {report.params.toDate && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>To:</strong> {formatDate(report.params.toDate)}
                    </Typography>
                  )}
                </>
              )}
            </Box>

            {report.status === ReportJobStatus.COMPLETED && csvFile && (
              <ExportFileTypesByClientReportButton reportId={reportId} csvFile={csvFile} />
            )}
          </Box>

          {report.status === ReportJobStatus.FAILED && report.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Error:</strong> {report.error.message}
              </Typography>
            </Alert>
          )}

          {(report.status === ReportJobStatus.PENDING || report.status === ReportJobStatus.RUNNING) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Report is being generated. This page will automatically refresh when the report is ready.
              </Typography>
            </Alert>
          )}
        </Box>

        {report.status === ReportJobStatus.COMPLETED && jsonFile && jsonFile.s3Key && (
          <FileTypesByClientReportTable reportId={reportId} jsonFile={jsonFile} />
        )}

        {report.status === ReportJobStatus.COMPLETED && (!jsonFile || !jsonFile.s3Key) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Report data is not available for viewing. The JSON file may not have been generated properly.
            </Typography>
          </Alert>
        )}
      </Box>
    </PageLayout>
  );
};

export { FileTypesByClientReportDetailsPage };
